import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DocIcon, FolderIcon, TrashIcon, isDefaultDocIcon } from '../../../icons';
import { renderIcon } from '../../../../SprintDetail/FlowchartView/utils/renderIcon';
import { useContextMenu } from '../../../hooks';
import { getFolderPath } from '../../../../../utils/docOrganization';
import { DOC_DRAG_TYPE } from '../constants';
import { sidebarTreeIndent } from '../../../utils/treeIndent';
import { copyDocMenuLabel, copyDocToClipboard } from '../../../../../utils/docClipboard';

const MOVE_RESULT_LIMIT = 20;

const isAppIcon = (icon) =>
  icon && typeof icon === 'object' && (icon.type === 'fontawesome' || icon.type === 'custom');

const SidebarDocMark = ({ icon, size = 16 }) => {
  if (isDefaultDocIcon(icon)) return <DocIcon size={size} />;
  if (isAppIcon(icon)) {
    return renderIcon(icon, { size: `${size}px`, fallback: <DocIcon size={size} /> });
  }
  if (typeof icon === 'object' && icon.type === 'emoji' && icon.value) {
    return <span className="sidebar__item-emoji">{icon.value}</span>;
  }
  if (typeof icon === 'string') {
    return <span className="sidebar__item-emoji">{icon}</span>;
  }
  return <DocIcon size={size} />;
};

const DocRow = ({
  doc,
  depth = 0,
  selected = false,
  onSelect,
  onOpenInNewTab,
  onDeleteDoc,
  organization,
}) => {
  const { contextMenu, openContextMenu, closeContextMenu } = useContextMenu();
  const {
    ready,
    folders,
    getDocFolderId,
    moveDocToFolder,
  } = organization;

  const currentFolderId = getDocFolderId(doc.id);
  const [moveOpen, setMoveOpen] = useState(false);
  const [folderQuery, setFolderQuery] = useState('');
  const moveInputRef = useRef(null);

  useEffect(() => {
    if (!contextMenu) {
      setMoveOpen(false);
      setFolderQuery('');
    }
  }, [contextMenu]);

  useEffect(() => {
    if (!moveOpen) return;
    const input = moveInputRef.current;
    if (!input) return;
    input.focus();
  }, [moveOpen]);

  const folderOptions = useMemo(() => {
    return (folders || []).map((f) => {
      const path = getFolderPath(folders, f.id);
      const label = path.map((p) => p.name).join(' / ') || f.name;
      return { id: f.id, label, name: f.name };
    });
  }, [folders]);

  const moveMatches = useMemo(() => {
    const q = folderQuery.trim().toLowerCase();
    if (!q) return [];
    return folderOptions
      .filter((f) => f.label.toLowerCase().includes(q) || f.name.toLowerCase().includes(q))
      .slice(0, MOVE_RESULT_LIMIT);
  }, [folderOptions, folderQuery]);

  const moveTo = (folderId) => {
    closeContextMenu();
    moveDocToFolder(doc.id, folderId);
  };

  const handleDragStart = (e) => {
    e.dataTransfer.setData(DOC_DRAG_TYPE, doc.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect?.(doc.id);
    }
  };

  const indentPx = sidebarTreeIndent(depth);

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className={`sidebar__item sidebar__docs-item ${depth > 0 ? 'sidebar__item--nested' : ''} ${selected ? 'sidebar__item--selected' : ''}`}
        style={{ paddingLeft: indentPx }}
        onClick={() => onSelect?.(doc.id)}
        onMouseDown={(e) => {
          if (e.button !== 1) return;
          e.preventDefault();
          e.stopPropagation();
          onOpenInNewTab?.(doc, 'doc');
        }}
        onKeyDown={handleKeyDown}
        onContextMenu={openContextMenu}
        draggable
        onDragStart={handleDragStart}
        title={doc.title || 'Untitled Doc'}
      >
        <div className="sidebar__item-row">
          <span
            className={`sidebar__item-icon-container ${isDefaultDocIcon(doc.icon) ? '' : 'sidebar__item-icon-container--custom'}`}
            aria-hidden
          >
            <span className="sidebar__item-icon">
              <SidebarDocMark icon={doc.icon} size={16} />
            </span>
          </span>
          <span className="sidebar__item-label">{doc.title || 'Untitled Doc'}</span>
        </div>
      </div>

      {contextMenu &&
        createPortal(
          <div
            className="sidebar__context-menu sidebar__context-menu--doc"
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="sidebar__context-menu-item"
              onClick={() => {
                closeContextMenu();
                onOpenInNewTab?.(doc, 'doc');
              }}
            >
              Open in new tab
            </button>
            <button
              type="button"
              className="sidebar__context-menu-item"
              onClick={() => {
                copyDocToClipboard(doc);
                closeContextMenu();
              }}
            >
              {copyDocMenuLabel(doc.title)}
            </button>
            <button
              type="button"
              className="sidebar__context-menu-item"
              disabled={!ready}
              onClick={() => setMoveOpen((v) => !v)}
            >
              Move
            </button>
            {moveOpen && (
              <div className="sidebar__context-menu-move">
                <input
                  ref={moveInputRef}
                  type="text"
                  className="sidebar__context-menu-search"
                  value={folderQuery}
                  placeholder="Type a folder name…"
                  onChange={(e) => setFolderQuery(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setMoveOpen(false);
                      setFolderQuery('');
                    } else if (e.key === 'Enter' && moveMatches[0]) {
                      e.preventDefault();
                      moveTo(moveMatches[0].id);
                    }
                  }}
                />
                {currentFolderId && (
                  <button
                    type="button"
                    className="sidebar__context-menu-item"
                    disabled={!ready}
                    onClick={() => moveTo(null)}
                  >
                    Unfiled
                  </button>
                )}
                {!folderQuery.trim() && (
                  <div className="sidebar__context-menu-hint">
                    {folderOptions.length === 0
                      ? 'No folders yet — use New folder'
                      : 'Type to find a folder'}
                  </div>
                )}
                {folderQuery.trim() && moveMatches.length === 0 && (
                  <div className="sidebar__context-menu-hint">No matching folders</div>
                )}
                {moveMatches.map((folder) => (
                  <button
                    key={folder.id}
                    type="button"
                    className="sidebar__context-menu-item"
                    disabled={!ready}
                    onClick={() => moveTo(folder.id === currentFolderId ? null : folder.id)}
                  >
                    <FolderIcon size={11} />
                    <span className="sidebar__context-menu-item-grow">{folder.label}</span>
                    {currentFolderId === folder.id && <span aria-hidden>✓</span>}
                  </button>
                ))}
              </div>
            )}
            <div className="sidebar__context-menu-divider" />
            <button
              type="button"
              className="sidebar__context-menu-item sidebar__context-menu-item--danger"
              onClick={() => {
                closeContextMenu();
                onDeleteDoc?.(doc.id);
              }}
            >
              <TrashIcon size={12} />
              Move to Trash
            </button>
          </div>,
          document.body
        )}
    </>
  );
};

export default DocRow;
